import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core/styles'
import SydInput from '../../../../../commonDesign/SydInput'

const useStyles = makeStyles(() => ({
  root: {
    border: '1px solid var(--color-neutral-200)',
    borderRadius: '8px',
    overflow: 'hidden'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 12px',
    backgroundColor: 'var(--color-neutral-050)',
    borderBottom: '1px solid var(--color-neutral-200)',
    cursor: 'pointer',
    userSelect: 'none',
    '&:hover': {
      backgroundColor: 'var(--color-neutral-100)'
    }
  },
  headerTitle: {
    fontWeight: 600,
    fontSize: '12px',
    color: 'var(--text-color-primary)',
    display: 'flex',
    alignItems: 'center',
    gap: '6px'
  },
  count: {
    fontSize: '10px',
    color: 'var(--text-color-subtle)',
    backgroundColor: 'var(--color-neutral-200)',
    borderRadius: '8px',
    padding: '0 5px',
    lineHeight: '16px'
  },
  addBtn: {
    fontSize: '11px',
    color: 'var(--color-primary-500)',
    cursor: 'pointer',
    border: 'none',
    background: 'none',
    fontWeight: 600,
    '&:hover': {
      color: 'var(--color-primary-700)'
    }
  },
  body: {
    padding: '8px'
  },
  empty: {
    color: 'var(--text-color-subtle)',
    fontSize: '11px',
    textAlign: 'center',
    padding: '12px 8px'
  },
  row: {
    display: 'flex',
    gap: '6px',
    alignItems: 'center',
    '&:not(:last-child)': {
      marginBottom: '4px'
    }
  },
  rowInput: {
    flex: 1
  },
  arrow: {
    color: 'var(--color-neutral-400)',
    fontSize: '11px',
    flexShrink: 0
  },
  removeBtn: {
    width: '22px',
    height: '22px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    color: 'var(--color-neutral-400)',
    fontSize: '11px',
    borderRadius: '4px',
    flexShrink: 0,
    '&:hover': {
      backgroundColor: 'var(--color-danger-050)',
      color: 'var(--color-danger-500)'
    }
  }
}))

function ArgMappingEditor ({ argMapping, onChange }) {
  const classes = useStyles()
  const [expanded, setExpanded] = useState(true)

  const entries = useMemo(() => {
    return Object.entries(argMapping || {}).map(([key, value]) => ({ key, value }))
  }, [argMapping])

  const handleAdd = useCallback((e) => {
    e.stopPropagation()
    if (Object.prototype.hasOwnProperty.call(argMapping || {}, '')) return
    onChange({ ...(argMapping || {}), '': '' })
    setExpanded(true)
  }, [argMapping, onChange])

  const handleRemove = useCallback((keyToRemove) => {
    const next = { ...(argMapping || {}) }
    delete next[keyToRemove]
    onChange(next)
  }, [argMapping, onChange])

  const handleUpdate = useCallback((oldKey, newKey, newValue) => {
    if (newKey !== oldKey && Object.prototype.hasOwnProperty.call(argMapping || {}, newKey)) return
    const next = {}
    for (const [k, v] of Object.entries(argMapping || {})) {
      if (k === oldKey) {
        next[newKey] = newValue
      } else {
        next[k] = v
      }
    }
    onChange(next)
  }, [argMapping, onChange])

  return (
    <div className={classes.root}>
      <div className={classes.header} onClick={() => setExpanded(!expanded)}>
        <span className={classes.headerTitle}>
          Arg Mapping
          <span className={classes.count}>{entries.length}</span>
        </span>
        <button type='button' className={classes.addBtn} onClick={handleAdd}>+ Add</button>
      </div>
      {expanded && (
        <div className={classes.body}>
          {entries.length === 0 ? (
            <div className={classes.empty}>Maps form field values to pipeline arguments</div>
          ) : (
            entries.map((entry, index) => (
              <div key={`${index}_${entry.key}`} className={classes.row}>
                <div className={classes.rowInput}>
                  <SydInput
                    value={entry.key}
                    onChange={(e) => handleUpdate(entry.key, e.target.value, entry.value)}
                    placeholder='argName'
                    size='sm'
                  />
                </div>
                <span className={classes.arrow}>&rarr;</span>
                <div className={classes.rowInput}>
                  <SydInput
                    value={entry.value}
                    onChange={(e) => handleUpdate(entry.key, entry.key, e.target.value)}
                    placeholder='$.fieldId'
                    size='sm'
                  />
                </div>
                <button type='button' className={classes.removeBtn} onClick={() => handleRemove(entry.key)}>&#10005;</button>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  )
}

ArgMappingEditor.propTypes = {
  argMapping: PropTypes.object,
  onChange: PropTypes.func.isRequired
}

export default ArgMappingEditor
